import { MasterChef } from '@jdiyield/abi'
import {
  APEROCKET,
  APEROCKET_POLYGON,
  APEZONE_POOLS,
  AUTO_CAKE_POOL,
  AUTO_POLYGON,
  backendUrl,
  BEEFY,
  BFT,
  BUNNY,
  DINOPARK_LV1,
  DOPPLE,
  FOODCOURT_POOL,
  HORIZON,
  IRON_V2,
  KILLSWITCH_VAULT,
  MERLINLAB,
  SEEDER_POOL,
  SUSHI_POOL,
  TWINDEX
} from '@jdiyield/constants'
import { api } from '@jdiyield/utils'
import BigNumber from 'bignumber.js'

import apeZoneABI from './abi/ApeZone.json'
import { getRewardApeRocket, stakeApeRocket, unStakeApeRocket } from './platforms/aperocket'
import { getRewardAutoFarm } from './platforms/autoFarm'
import { getRewardBeefy, stakeBeefy, unStakeBeefy } from './platforms/beefy'
import { DINOPRAK_ABI } from './platforms/dinopark/dinoparkPool'
import { stakeDoppple, unStakeDopple } from './platforms/dopple'
import { getRewardHZN, stakeHZN, unStakeHZN } from './platforms/horizon'
import { stakeKSW, unStakeKSW } from './platforms/killswitch'
import { getRewardMerlinLab, stakeMerlinLab, unStakeMerlinLab } from './platforms/merlinlab'
import { getRewardBunny, stakeBunny, unStakeBunny } from './platforms/pancakeBunny'
import { stakeSeeder, unStakeSeeder } from './platforms/seeder'
import { harvestTwindex, stakeTwindex, unStakeTwindex } from './platforms/twindex'
import { harvestMiniChef, stakeMiniChef, unStakeMiniChef } from './poolType/miniChef'
import { ImpermanentLoss, StakeFunc, StakeProps } from './types'
import { safeGasEstimates } from './utils'

export const BIG_TEN = new BigNumber(10)

export const getDecimalAmount = (amount: BigNumber, decimals = 18) => {
  return new BigNumber(amount).times(BIG_TEN.pow(decimals))
}

export const getBalanceAmount = (amount: BigNumber, decimals = 18) => {
  return new BigNumber(amount).dividedBy(BIG_TEN.pow(decimals))
}

export const getBalanceNumber = (balance: BigNumber, decimals = 18) => {
  return getBalanceAmount(balance, decimals).toString()
}

export async function getIL(account: string, lpAddress: string, lpBalance: number, price0: number, price1: number) {
  const il = await impermanentLoss(account, lpAddress, lpBalance)

  if (il !== null && il.totalCurrentValue === undefined && price0 !== undefined && price1 !== undefined) {
    const depositToken0Value = il.token0.deposit * price0
    const currentToken0Value = il.token0.current * price0

    const depositToken1Value = il.token1.deposit * price1
    const currentToken1Value = il.token1.current * price1

    const token0ValueChange = currentToken0Value - depositToken0Value
    const token1ValueChange = currentToken1Value - depositToken1Value

    const totalDepositValue = depositToken0Value + depositToken1Value
    const totalCurrentValue = currentToken0Value + currentToken1Value
    const totalValueChange = totalCurrentValue - totalDepositValue

    il.token0.valueChange = token0ValueChange
    il.token1.valueChange = token1ValueChange
    il.totalDepositValue = totalDepositValue
    il.totalCurrentValue = totalCurrentValue
    il.totalValueChange = totalValueChange
    il.totalValueChangeInPercent = (totalValueChange / totalDepositValue) * 100
  }

  return il
}

export const impermanentLoss = async (
  walletAddress: string,
  lpAddress: string,
  depositLpBalance: number
): Promise<ImpermanentLoss> => {
  let data: ImpermanentLoss
  try {
    const res = await api.post(`${backendUrl}/impermanent-loss`, {
      walletAddress,
      lpAddress,
      depositLpBalance
    })
    data = res.data
  } catch {
    data = null
  }
  return data
}

export const harvest: StakeFunc<Omit<StakeProps, 'amount'>> = async ({
  web3,
  pool,
  contractAddress,
  walletAddress,
  typePool = 'LP_STAKING'
}) => {
  try {
    switch (contractAddress) {
      case BUNNY:
        return await getRewardBunny({ pool, contractAddress, walletAddress, web3 })
      case MERLINLAB:
        return await getRewardMerlinLab({ pool, contractAddress, walletAddress, web3 })
      case BEEFY:
        return await getRewardBeefy({ pool, contractAddress, walletAddress, web3 })
      case HORIZON:
        return await getRewardHZN({ pool, contractAddress, walletAddress, web3 })
      case DOPPLE:
        return await unStakeDopple({ web3, pool, contractAddress, walletAddress, amount: '0' })
      case TWINDEX:
        return await harvestTwindex({ web3, pool, contractAddress, walletAddress })
      case SUSHI_POOL:
      case IRON_V2:
        return await harvestMiniChef({ web3, pool, contractAddress, walletAddress })
      case AUTO_POLYGON:
        return await getRewardAutoFarm({ web3, pool, walletAddress })
      default:
        break
    }

    if (APEROCKET.includes(contractAddress) || APEROCKET_POLYGON.includes(contractAddress)) {
      return await getRewardApeRocket({ web3, pool, contractAddress, walletAddress })
    }

    const result = await unStake({ web3, pool, contractAddress, walletAddress, amount: '0', typePool })
    return result
  } catch (e) {
    return false
  }
}

// stake or deposit
export const stake: StakeFunc = async ({
  web3,
  pool,
  contractAddress,
  walletAddress,
  amount,
  typePool = 'LP_STAKING'
}) => {
  try {
    // await window.ethereum.enable()

    switch (contractAddress) {
      case SEEDER_POOL:
        return await stakeSeeder({ web3, pool, contractAddress, walletAddress, amount })
      case BUNNY:
        return await stakeBunny({ web3, pool, contractAddress, walletAddress, amount })
      case MERLINLAB:
        return await stakeMerlinLab({ web3, pool, contractAddress, walletAddress, amount })
      case BEEFY:
        return await stakeBeefy({ web3, pool, contractAddress, walletAddress, amount })
      case HORIZON:
        return await stakeHZN({ web3, pool, contractAddress, walletAddress, amount })
      case DOPPLE:
        return await stakeDoppple({ web3, pool, contractAddress, walletAddress, amount })
      case TWINDEX:
        return await stakeTwindex({ web3, pool, contractAddress, walletAddress, amount })
      case SUSHI_POOL:
      case IRON_V2:
        return await stakeMiniChef({ web3, pool, contractAddress, walletAddress, amount })
      case KILLSWITCH_VAULT:
        return await stakeKSW({ web3, pool, contractAddress, walletAddress, amount })
      default:
        break
    }

    if (APEROCKET.includes(contractAddress) || APEROCKET_POLYGON.includes(contractAddress)) {
      return await stakeApeRocket({ web3, pool, contractAddress, walletAddress, amount })
    }

    if (DINOPARK_LV1.includes(contractAddress)) {
      const ct = new web3.eth.Contract(DINOPRAK_ABI as any, contractAddress)
      const decimals = {
        [DINOPARK_LV1[0]]: 1e18,
        [DINOPARK_LV1[1]]: 1e9,
        [DINOPARK_LV1[2]]: 1e18,
        [DINOPARK_LV1[3]]: 1e9
      }
      const contractWithMethod = ct.methods.deposit(new BigNumber(amount).times(decimals[contractAddress]).toFixed())
      const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })

      await contractWithMethod.send({ from: walletAddress, gas: gasLimit })

      return true
    }
    if (APEZONE_POOLS.includes(contractAddress)) {
      const ct = new web3.eth.Contract(apeZoneABI as any, contractAddress)
      const contractWithMethod = ct.methods.deposit(new BigNumber(amount).times(new BigNumber(10).pow(18)).toFixed())
      const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })

      await contractWithMethod.send({ from: walletAddress, gas: gasLimit })

      return true
    }

    if (typePool === 'TOKEN_STAKING' && contractAddress !== FOODCOURT_POOL) {
      if (contractAddress === BFT || contractAddress === AUTO_CAKE_POOL || pool < 0) {
        //TODO REFACTOR ??
        const ct = new web3.eth.Contract(apeZoneABI as any, contractAddress)
        const contractWithMethod = ct.methods.deposit(new BigNumber(amount).times(new BigNumber(10).pow(18)).toFixed())
        const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })

        await contractWithMethod.send({ from: walletAddress, gas: gasLimit })
      } else {
        const ct = new web3.eth.Contract(MasterChef.abi as any, contractAddress)
        const contractWithMethod = ct.methods.enterStaking(
          new BigNumber(amount).times(new BigNumber(10).pow(18)).toFixed()
        )
        const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })

        await contractWithMethod.send({ from: walletAddress, gas: gasLimit })
      }
    } else {
      const ct = new web3.eth.Contract(MasterChef.abi as any, contractAddress)
      const contractWithMethod = ct.methods.deposit(
        pool,
        new BigNumber(amount).times(new BigNumber(10).pow(18)).toFixed()
      )
      const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })

      await contractWithMethod.send({ from: walletAddress, gas: gasLimit })
    }
    return true
  } catch (e) {
    return false
  }
}

// unstake or withdraw
export const unStake: StakeFunc = async ({
  web3,
  pool,
  contractAddress,
  walletAddress,
  amount,
  typePool = 'LP_STAKING'
}) => {
  try {
    // await window.ethereum.enable()
    switch (contractAddress) {
      case SEEDER_POOL:
        return await unStakeSeeder({ web3, pool, contractAddress, walletAddress, amount })
      case BUNNY:
        return await unStakeBunny({ web3, pool, contractAddress, walletAddress, amount })
      case MERLINLAB:
        return await unStakeMerlinLab({ web3, pool, contractAddress, walletAddress, amount })
      case BEEFY:
        return await unStakeBeefy({ web3, pool, contractAddress, walletAddress, amount })
      case HORIZON:
        return await unStakeHZN({ web3, pool, contractAddress, walletAddress, amount })
      case DOPPLE:
        return await unStakeDopple({ web3, pool, contractAddress, walletAddress, amount })
      case TWINDEX:
        return await unStakeTwindex({ web3, pool, contractAddress, walletAddress, amount })
      case SUSHI_POOL:
      case IRON_V2:
        return await unStakeMiniChef({ web3, pool, contractAddress, walletAddress, amount })
      case KILLSWITCH_VAULT:
        return await unStakeKSW({ web3, pool, contractAddress, walletAddress, amount })
      default:
        break
    }
    if (APEROCKET.includes(contractAddress) || APEROCKET_POLYGON.includes(contractAddress)) {
      return await unStakeApeRocket({ web3, pool, contractAddress, walletAddress, amount })
    }

    if (DINOPARK_LV1.includes(contractAddress)) {
      const ct = new web3.eth.Contract(DINOPRAK_ABI as any, contractAddress)
      const decimals = {
        [DINOPARK_LV1[0]]: 1e18,
        [DINOPARK_LV1[1]]: 1e9,
        [DINOPARK_LV1[2]]: 1e18,
        [DINOPARK_LV1[3]]: 1e9
      }

      const contractWithMethod = ct.methods.withdraw(new BigNumber(amount).times(decimals[contractAddress]).toFixed())
      const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })

      await contractWithMethod.send({ from: walletAddress, gas: gasLimit })
      return true
    }

    if (APEZONE_POOLS.includes(contractAddress)) {
      const ct = new web3.eth.Contract(apeZoneABI as any, contractAddress)

      const contractWithMethod = ct.methods.withdraw(new BigNumber(amount).times(new BigNumber(10).pow(18)).toFixed())
      const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })

      await contractWithMethod.send({ from: walletAddress, gas: gasLimit })
      return true
    }

    if (typePool === 'TOKEN_STAKING' && contractAddress !== FOODCOURT_POOL) {
      const ct = new web3.eth.Contract(MasterChef.abi as any, contractAddress)
      const contractWithMethod = ct.methods.leaveStaking(
        new BigNumber(amount).times(new BigNumber(10).pow(18)).toFixed()
      )
      const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })

      await contractWithMethod.send({ from: walletAddress, gas: gasLimit })
    } else {
      const ct = new web3.eth.Contract(MasterChef.abi as any, contractAddress)
      const contractWithMethod = ct.methods.withdraw(
        pool,
        new BigNumber(amount).times(new BigNumber(10).pow(18)).toFixed()
      )
      const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })

      await contractWithMethod.send({ from: walletAddress, gas: gasLimit })
    }

    return true
  } catch (e) {
    console.log(e)
    return false
  }
}

interface registerTokenWithWalletProps {
  address: string
  symbol: string
  decimals?: number
  image: string
}
export const registerTokenWithWallet = async ({
  address,
  symbol,
  decimals = 18,
  image
}: registerTokenWithWalletProps): Promise<boolean> => {
  try {
    // wasAdded is a boolean. Like any RPC method, an error may be thrown.
    const wasAdded = await window['ethereum'].request({
      method: 'wallet_watchAsset',
      params: {
        type: 'ERC20', // Initially only supports ERC20, but eventually more!
        options: {
          address: address,
          symbol: symbol,
          decimals: decimals,
          image: image
        }
      }
    })

    if (wasAdded) {
      return true
    } else {
      return false
    }
  } catch (e) {
    return false
  }
}
